/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box, Container, Grid, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { ROUTE } from "../Interface/interface";
import { Context } from "../App";
import AceEditor from "react-ace";
import ReactJson from "react-json-view";
import cssbeautify from "cssbeautify";
import DataSettings from "./dataSettings";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/mode-css";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";
import { prettify } from "htmlfy";

const ace = require("ace-builds/src-noconflict/ace");
ace.config.set("basePath", "https://cdn.jsdelivr.net/npm/ace-builds@1.4.3/src-noconflict/");
ace.config.setModuleUrl("ace/mode/json_worker", "https://cdn.jsdelivr.net/npm/ace-builds@1.4.3/src-noconflict/worker-json.js");
ace.config.setModuleUrl("ace/mode/html_worker", "https://cdn.jsdelivr.net/npm/ace-builds@1.4.3/src-noconflict/worker-html.js");
ace.config.setModuleUrl("ace/mode/css_worker", "https://cdn.jsdelivr.net/npm/ace-builds@1.4.3/src-noconflict/worker-css.js");

export const publicURL = window.location.href.includes("localhost:8080")
  ? "http://localhost:8080/testing/"
  : window.location.href.includes("localhost:3000")
  ? "http://localhost:3000/"
  : "https://www.smartmobsolution.com/";

const DataFormatter = () => {
  const { setInputCode, setBeautifiedCode, beautifiedCode, setUrlType, urlType, inputcode, showJsonDataType, showObjectSize, inputFileData, setInputFileData, htmlRunView } = useContext(Context);

  // Inside the Formator component
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tempJson, setTempJson] = useState({});
  const jsonViewOptions = {
    controls: false,
    enableClipboard: false,
    displayDataTypes: showJsonDataType,
    displayObjectSize: showObjectSize,
    name: null,
  };

  const [inputError, setInputError] = useState<boolean>(false);

  const handleCodeChange = (newCode: string) => {
    try {
      setInputFileData(newCode);
      setInputCode(newCode);
      const url = window.location.href;
      if (urlType) {
        if (urlType === ROUTE.HTML_FORMATOR) htmlBeautifyCode(newCode);
        else if (urlType === ROUTE.CSS_FORMATOR) cssBeautifyCode(newCode);
        else if (urlType === ROUTE.JSON_FORMATOR) jsonFormatterCode(newCode);
        else if (urlType === ROUTE.JSON_PARSER) jsonParserCode(newCode);
      }
      setErrorMessage(null);
      setInputError(false);
    } catch (error: any) {
      setErrorMessage(`${error.message}`);
    }
    if (newCode === "") {
      setBeautifiedCode("");
      setTempJson("");
      setErrorMessage(null);
    }
  };

  const htmlBeautifyCode = (inputData: any) => {
    const formattedCode = prettify(inputData);
    setBeautifiedCode(formattedCode);
  };

  const cssBeautifyCode = (inputData: any) => {
    try {
      const beautifiedCss = cssbeautify(inputData, {
        indent: "\t", // You can adjust the number of spaces or use '\t' for tabs
        openbrace: "end-of-line",
      });
      setBeautifiedCode(beautifiedCss);
      setErrorMessage(null);
    } catch (error: any) {
      setErrorMessage("Css Syntext Error.");
    }
  };

  const jsonParserCode = (inputData: any) => {
    const parserJson = JSON.parse(inputData);
    setTempJson(parserJson);
  };

  const jsonFormatterCode = (inputData: any) => {
    const formattedJson = JSON.parse(inputData);
    setBeautifiedCode(JSON.stringify(formattedJson, null, 2));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setErrorMessage(null);
      readFileContents(file);
      setTimeout(() => {
        checkInputError();
      }, 200);
    }
  };

  const readFileContents = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const fileContent = e?.target?.result as string;
        handleCodeChange(fileContent);
      } catch (error: any) {
        if (error instanceof SyntaxError) {
          setErrorMessage("Invalid JSON format. Please provide a valid JSON file.");
        } else {
          setErrorMessage(`${error.message}`);
        }
        console.error("Error reading file:", error);
      }
    };
    reader.readAsText(file);
  };

  useEffect(() => {
    const url = window.location.href;
    if (url) {
      if (url.includes(ROUTE.HTML_FORMATOR)) setUrlType(ROUTE.HTML_FORMATOR);
      else if (url.includes(ROUTE.CSS_FORMATOR)) setUrlType(ROUTE.CSS_FORMATOR);
      else if (url.includes(ROUTE.JSON_FORMATOR)) setUrlType(ROUTE.JSON_FORMATOR);
      else setUrlType(ROUTE.JSON_PARSER);
    }
  }, []);

  useEffect(() => {
    try {
      if (urlType) {
        if (urlType === ROUTE.HTML_FORMATOR) htmlBeautifyCode(inputcode);
        else if (urlType === ROUTE.CSS_FORMATOR) cssBeautifyCode(inputcode);
        else if (urlType === ROUTE.JSON_FORMATOR) jsonFormatterCode(inputcode);
        else if (urlType === ROUTE.JSON_PARSER) jsonParserCode(inputcode);
      }

      setErrorMessage(null);
    } catch (error: any) {
      console.log("error", error);
    }
  }, [urlType]);

  const checkInputError = () => {
    const inputEditor: any = document.getElementById("code-editor");
    const divElements = document.querySelectorAll("div");
    const targetClass = "ace_error";
    let containsClass = false;

    divElements.forEach((divElement) => {
      if (divElement.classList.contains(targetClass)) {
        containsClass = true;
      }
    });

    if (containsClass) {
      setInputError(true);
    } else {
      setInputError(false);
    }
  };

  // Use useEffect to check for input errors when input data changes
  useEffect(() => {
    setTimeout((res: any) => {
      checkInputError();
    }, 500);
  }, [inputcode]);

  return (
    <>
      <Container maxWidth="md" className="json-parser-tool" sx={{ marginY: 1, padding: "0 !important" }}>
        <Box className="" display="flex" alignItems="center" justifyContent="space-between">
          <Box className="error">{errorMessage && <Typography className="error">{errorMessage}</Typography>}</Box>
          <DataSettings handleFileUpload={handleFileUpload} setErrorMessage={setErrorMessage} inputError={inputError} />
        </Box>
        <Box className="" display="flex" alignItems="center" justifyContent="space-between" mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" fontWeight="bold" marginY={2.5}>
                Beautify Input {urlType}
              </Typography>
              <Box className="json-area">
                <AceEditor
                  mode={urlType === ROUTE.HTML_FORMATOR ? "html" : urlType === ROUTE.CSS_FORMATOR ? "css" : "json"}
                  theme="github"
                  focus={true}
                  onChange={handleCodeChange}
                  name="code-editor"
                  editorProps={{ $blockScrolling: true }}
                  setOptions={{
                    useWorker: true,
                    wrap: true,
                  }}
                  value={inputFileData}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6} className={`${errorMessage ? "show-error" : ""}`}>
              <Typography variant="h5" fontWeight="bold" marginY={2.5}>
                Beautified Output
              </Typography>
              {urlType === ROUTE.JSON_PARSER ? (
                <Box className="json-area">{errorMessage ? <div className="error-message">{errorMessage}</div> : <ReactJson src={tempJson} {...jsonViewOptions} />}</Box>
              ) : (
                <div className={`json-area ${htmlRunView && !inputError ? "showHTMLView" : ""}`}>
                  {/* {urlType === ROUTE.HTML_FORMATOR || urlType === ROUTE.CSS_FORMATOR
                    ? inputError && <div className="error-message">There are any Syntax Error occurs.</div>
                    : errorMessage && <div className="error-message">{errorMessage}</div>} */}
                  {errorMessage && <div className="error-message">{errorMessage}</div>}
                  <AceEditor
                    mode={urlType === ROUTE.HTML_FORMATOR ? "html" : urlType === ROUTE.CSS_FORMATOR ? "css" : "json"}
                    theme="github"
                    value={beautifiedCode}
                    name="beautified-editor"
                    editorProps={{ $blockScrolling: true }}
                    readOnly
                    setOptions={{
                      useWorker: false,
                      wrap: true,
                    }}
                  />
                  <div className="html-view-code" dangerouslySetInnerHTML={{ __html: beautifiedCode }} />
                </div>
              )}
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default DataFormatter;
